import { motion } from "framer-motion";
import { MapPin, Calendar, ArrowRight, CheckCircle2 } from "lucide-react";
import SceneShell from "../components/SceneShell";

/**
 * Scene 31 — Ready to onboard · the unlock.
 * Right pane: stylised map showing Ashtons in Harpenden with a location pin.
 * The "Mark securing access to WMS" line moved to script. Same with "this isn't
 * theory anymore" — script.
 */

export default function Scene31_TheUnlock() {
  return (
    <SceneShell variant="ink" align="left" data-testid="scene-the-unlock">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 0.3 }}
        style={{
          background:
            "radial-gradient(ellipse at 70% 50%, rgba(150,193,31,0.18) 0%, transparent 55%)",
        }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="kn-eyebrow mb-6"
          >
            Let's go!
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="kn-display text-5xl sm:text-7xl lg:text-[100px] text-white leading-[0.95]"
            data-testid="unlock-title"
          >
            We are now ready to{" "}
            <span
              style={{
                background:
                  "linear-gradient(135deg, #96C11F 0%, #00deb6 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              onboard.
            </span>
          </motion.h2>

          {/* First meeting pill */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 1.4 }}
            className="mt-10 inline-flex items-center gap-4 rounded-2xl px-6 py-4 glass-dark"
            data-testid="first-meeting-pill"
          >
            <span
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{
                background: "rgba(150,193,31,0.18)",
                border: "1px solid rgba(150,193,31,0.45)",
                color: "#96C11F",
              }}
            >
              <Calendar className="w-5 h-5" />
            </span>
            <div>
              <div
                className="text-[10px] tracking-[0.32em] uppercase mb-1"
                style={{ color: "#96C11F" }}
              >
                First meeting
              </div>
              <div
                className="kn-display text-lg sm:text-xl text-white"
                style={{ fontWeight: 700 }}
              >
                Ashtons · Harpenden ·{" "}
                <span style={{ color: "#96C11F" }}>18 May</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right pane — stylised map of Harpenden with location pin */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-3xl overflow-hidden glass aspect-square"
            data-testid="harpenden-map"
          >
            {/* Map base */}
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 400 400"
              preserveAspectRatio="xMidYMid slice"
              aria-hidden="true"
            >
              <defs>
                <pattern
                  id="map-grid"
                  width="40"
                  height="40"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 40 0 L 0 0 0 40"
                    fill="none"
                    stroke="rgba(150,193,31,0.15)"
                    strokeWidth="0.5"
                  />
                </pattern>
                <radialGradient id="map-glow" cx="50%" cy="50%" r="55%">
                  <stop offset="0%" stopColor="rgba(150,193,31,0.30)" />
                  <stop offset="100%" stopColor="rgba(150,193,31,0)" />
                </radialGradient>
              </defs>
              <rect width="400" height="400" fill="rgba(7,18,12,0.9)" />
              <rect width="400" height="400" fill="url(#map-grid)" />
              <rect width="400" height="400" fill="url(#map-glow)" />

              {/* Stylised roads */}
              <path
                d="M 0 240 Q 120 220 200 200 T 400 160"
                stroke="rgba(150,193,31,0.45)"
                strokeWidth="1.4"
                fill="none"
              />
              <path
                d="M 60 0 Q 90 120 200 200 Q 280 260 380 400"
                stroke="rgba(150,193,31,0.30)"
                strokeWidth="1.2"
                fill="none"
              />
              <path
                d="M 0 80 Q 80 110 200 200 Q 320 290 400 320"
                stroke="rgba(150,193,31,0.25)"
                strokeWidth="0.9"
                fill="none"
              />
              <path
                d="M 200 0 L 200 400"
                stroke="rgba(150,193,31,0.18)"
                strokeWidth="0.7"
                strokeDasharray="3 6"
                fill="none"
              />

              {/* Subtle building blocks */}
              {[
                [50, 280],
                [120, 260],
                [285, 130],
                [310, 250],
                [85, 310],
                [340, 180],
                [150, 60],
              ].map(([x, y], i) => (
                <rect
                  key={i}
                  x={x}
                  y={y}
                  width="14"
                  height="10"
                  rx="2"
                  fill="rgba(150,193,31,0.20)"
                  stroke="rgba(150,193,31,0.35)"
                  strokeWidth="0.6"
                />
              ))}
            </svg>

            {/* Pulsing ring at the pin */}
            {[0, 1].map((i) => (
              <motion.span
                key={i}
                className="absolute rounded-full pointer-events-none"
                style={{
                  left: "50%",
                  top: "50%",
                  width: 36,
                  height: 36,
                  border: "1.5px solid rgba(150,193,31,0.65)",
                  translate: "-50% -50%",
                }}
                animate={{ scale: [1, 4], opacity: [0.7, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeOut",
                  delay: 1.4 + i * 1.2,
                }}
              />
            ))}

            {/* The pin */}
            <motion.div
              initial={{ opacity: 0, y: -32, scale: 0.6 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                delay: 1.0,
                duration: 0.9,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="absolute"
              style={{
                left: "50%",
                top: "50%",
                translate: "-50% -100%",
              }}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, #96C11F 0%, #00deb6 100%)",
                  boxShadow: "0 8px 32px rgba(150,193,31,0.65)",
                  border: "2px solid rgba(255,255,255,0.95)",
                }}
              >
                <MapPin
                  className="w-6 h-6 text-white"
                  fill="white"
                  strokeWidth={2}
                />
              </div>
            </motion.div>

            {/* Location label tag */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8, duration: 0.7 }}
              className="absolute"
              style={{ left: "50%", top: "50%", translate: "-50% 24px" }}
            >
              <div
                className="rounded-lg px-3 py-1.5 text-xs whitespace-nowrap"
                style={{
                  background: "rgba(7,18,12,0.92)",
                  border: "1px solid rgba(150,193,31,0.55)",
                  color: "#e2e8f0",
                  fontWeight: 700,
                }}
              >
                <span style={{ color: "#96C11F" }}>Ashtons</span> · Harpenden
              </div>
            </motion.div>

            {/* Top-left badge */}
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: "#96C11F" }}
              />
              <span className="text-[10px] tracking-[0.32em] uppercase text-white/65">
                Pilot · live target
              </span>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.4, duration: 0.7 }}
              className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-[0.18em] uppercase"
              style={{
                background: "rgba(150,193,31,0.16)",
                border: "1px solid rgba(150,193,31,0.45)",
                color: "#96C11F",
              }}
            >
              <CheckCircle2 className="w-3 h-3" />
              Onboarding
              <ArrowRight className="w-3 h-3" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </SceneShell>
  );
}
